
















































































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { SysKBTOCDetailsModule } from '@/store/modules/sysKBTOCDetails';
import { SysKBContentListModule } from '@/store/modules/sysKBContentList';
import KBTOCDto from '@/models/KnowledgeBase/KBTOCDto';
import TOCSection from '@/models/KnowledgeBase/TOCSection';
import TOCNode from '@/models/KnowledgeBase/TOCNode'; // v4: Changed from TOCItem
import UpdateTOCDraftRequest from '@/models/KnowledgeBase/UpdateTOCDraftRequest';
import PublishContentRequest from '@/models/KnowledgeBase/PublishContentRequest';
import KBContentListItem from '@/models/KnowledgeBase/KBContentListItem';
import KBStatus from '@/models/KnowledgeBase/KBStatus';

const MAX_TOC_DEPTH = 4; // v4: Maximum nesting depth

// v4: Import recursive node editor
import TOCNodeListEditor from './TOCNodeListEditor.vue';

@Component({
  name: 'TOCEditor',
  components: {
    TOCNodeListEditor
  }
})
export default class TOCEditor extends Vue {
  @Prop({ required: true }) tocId!: string;

  private expandedPanels: number[] = [];
  private hasChanges = false;
  private saving = false;
  private publishing = false;
  private publishDialog = false;
  private publishMessage = '';
  private editTitle = '';
  private editSections: TOCSection[] = [];

  get toc(): KBTOCDto | null {
    return SysKBTOCDetailsModule.toc;
  }

  get availableContent(): KBContentListItem[] {
    const items = SysKBContentListModule.contentList?.items;
    if (!items) return [];
    return items.filter(item => item.status === KBStatus.Published);
  }

  // v4: Can publish if TOC exists, has draft, and NO unsaved changes
  get canPublish(): boolean {
    return this.toc?.draft != null && !this.hasChanges;
  }

  created() {
    // Load content list only - parent component loads the TOC
    this.loadContentList();
    // Initialize if TOC is already loaded
    if (this.toc) {
      this.initializeEditSections();
    }
  }

  @Watch('toc')
  onTocChanged() {
    // When TOC loads/changes, initialize the edit sections
    this.initializeEditSections();
  }

  async loadContentList() {
    await SysKBContentListModule.getContentList({ status: KBStatus.Published });
  }

  initializeEditSections() {
    if (!this.toc) return;

    const source = this.toc.draft || this.toc.published;
    if (source) {
      this.editTitle = source.title || '';
      if (source.structure) {
        this.editSections = JSON.parse(JSON.stringify(source.structure));
        this.expandedPanels = this.editSections.map((_, index) => index);
      } else {
        this.editSections = [];
      }
    } else {
      this.editTitle = '';
      this.editSections = [];
    }
    this.hasChanges = false;
  }

  markChanged() {
    this.hasChanges = true;
  }

  addSection() {
    const newSection = new TOCSection();
    newSection.label = 'New Section';
    newSection.icon = 'mdi-folder-outline';
    newSection.items = [];

    this.editSections.push(newSection);
    this.expandedPanels.push(this.editSections.length - 1);
    this.markChanged();
  }

  removeSection(index: number) {
    this.editSections.splice(index, 1);
    this.markChanged();
  }

  addItem(sectionIndex: number, parentItems?: TOCNode[]) {
    const items = parentItems || this.editSections[sectionIndex].items;
    const newNode = new TOCNode();
    newNode.type = 'article';
    newNode.contentId = '';
    newNode.order = items.length;

    items.push(newNode);
    this.markChanged();
  }

  // v4: Add folder to section or nested within another folder
  addFolder(sectionIndex: number, parentItems?: TOCNode[]) {
    const items = parentItems || this.editSections[sectionIndex].items;
    const newNode = new TOCNode();
    newNode.type = 'folder';
    newNode.label = 'New Folder';
    newNode.expanded = false;
    newNode.items = [];
    newNode.order = items.length;

    items.push(newNode);
    this.markChanged();
  }

  removeItem(sectionIndex: number, itemIndex: number, parentItems?: TOCNode[]) {
    const items = parentItems || this.editSections[sectionIndex].items;
    items.splice(itemIndex, 1);
    // Update order
    items.forEach((item, i) => {
      item.order = i;
    });
    this.markChanged();
  }

  // v4: Calculate depth of a node tree
  calculateDepth(nodes: TOCNode[], currentDepth = 1): number {
    let maxDepth = currentDepth;
    for (const node of nodes) {
      if (node.type === 'folder' && node.items && node.items.length > 0) {
        const childDepth = this.calculateDepth(node.items, currentDepth + 1);
        maxDepth = Math.max(maxDepth, childDepth);
      }
    }
    return maxDepth;
  }

  // v4: Check if we can add nested items at this depth
  canAddNestedItem(parentItems: TOCNode[], currentDepth: number): boolean {
    return currentDepth < MAX_TOC_DEPTH;
  }

  // v4: Get content title by ID
  getContentTitle(contentId?: string): string {
    if (!contentId) return '';
    const content = this.availableContent.find(c => c.id === contentId);
    return content?.title || contentId;
  }

  // v4: Flatten tree for display with depth information
  flattenNodes(nodes: TOCNode[], depth = 1): Array<{node: TOCNode; depth: number; path: number[]}> {
    const result: Array<{node: TOCNode; depth: number; path: number[]}> = [];
    nodes.forEach((node, index) => {
      result.push({ node, depth, path: [index] });
      if (node.type === 'folder' && node.items && node.items.length > 0) {
        const children = this.flattenNodes(node.items, depth + 1);
        children.forEach(child => {
          child.path = [index, ...child.path];
        });
        result.push(...children);
      }
    });
    return result;
  }

  resetChanges() {
    this.initializeEditSections();
  }

  async saveDraft() {
    this.saving = true;
    try {
      const request = new UpdateTOCDraftRequest();
      request.title = this.editTitle;
      request.structure = this.editSections;

      await SysKBTOCDetailsModule.updateTOCDraft({ tocId: this.tocId, request });
      this.hasChanges = false;
    } finally {
      this.saving = false;
    }
  }

  async publish() {
    this.publishing = true;
    try {
      const request = new PublishContentRequest();
      request.changeSummary = this.publishMessage;

      await SysKBTOCDetailsModule.publishTOC({ tocId: this.tocId, request });

      this.publishDialog = false;
      this.publishMessage = '';
      this.hasChanges = false;
      this.initializeEditSections();
    } finally {
      this.publishing = false;
    }
  }
}
